import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Facebook, Heart } from 'lucide-react';
import GoogleLogo from './GoogleLogo';
import { GOOGLE_REVIEWS } from '../lib/googleReviews';
import creditPhotoLogo from '../assets/credit-photo-logo.jpg';

const Footer = () => {
  return (
    <footer className="bg-stone-900 text-stone-300 py-20 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-3xl font-bold text-white mb-6 flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_irremplacable-revamp/artifacts/qyuankrr_Logo-irremplacable-blanc.png" 
                alt="Logo Irremplaçable" 
                className="h-24 w-auto object-contain"
              />
              <span className="self-center mt-2">Irremplaçable</span>
            </h3>
            <p className="text-stone-400 max-w-md mb-8 text-lg leading-relaxed">
              Soins animaliers à domicile personnalisés. Nos experts assurent le bien-être de vos compagnons avec amour et professionnalisme.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61572246714925"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-12 h-12 rounded-full bg-stone-800 flex items-center justify-center hover:bg-teal-900 transition-colors"
              >
                <Facebook size={22} />
              </a>
              <a
                href={GOOGLE_REVIEWS.viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Avis Google"
                className="w-12 h-12 rounded-full bg-white flex items-center justify-center hover:bg-stone-100 transition-colors"
              >
                <GoogleLogo className="w-6 h-6" />
              </a>
            </div>
            <a
              href={GOOGLE_REVIEWS.writeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-teal-400 hover:text-teal-300 transition-colors"
            >
              <span className="flex gap-0.5 text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <span key={i}>★</span>
                ))}
              </span>
              Laissez-nous un avis Google →
            </a>
          </div>

          <div>
            <h4 className="text-white font-bold mb-8 text-lg">Liens Rapides</h4>
            <ul className="space-y-4">
              {[
                { name: 'Accueil', path: '/' },
                { name: 'Services', path: '/services' },
                { name: 'Notre Équipe', path: '/equipe' },
                { name: 'Avis Clients', path: '/avis' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="hover:text-teal-400 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-8 text-lg">Contact</h4>
            <ul className="space-y-6">
              <li className="flex items-start gap-4 group">
                <div className="bg-stone-800 p-2 rounded-lg group-hover:bg-teal-900 transition-colors">
                  <Mail size={20} className="text-teal-500" />
                </div>
                <a href="mailto:tech.irremplacable@gmail.com" className="hover:text-white transition-colors pt-1">tech.irremplacable@gmail.com</a>
              </li>
              <li className="flex items-start gap-4 group">
                <div className="bg-stone-800 p-2 rounded-lg group-hover:bg-teal-900 transition-colors">
                  <Phone size={20} className="text-teal-500" />
                </div>
                <a href="tel:5144644834" className="pt-1 hover:text-white transition-colors"> (514) 464-4834</a>
              </li>
              <li className="flex items-start gap-4 group">
                <div className="bg-stone-800 p-2 rounded-lg group-hover:bg-teal-900 transition-colors">
                  <Phone size={20} className="text-teal-500" />
                </div>
                <a href="tel:4505432535" className="pt-1 hover:text-white transition-colors"> (450) 543-2535</a>
              </li>
              <li className="flex items-start gap-4 group">
                <div className="bg-stone-800 p-2 rounded-lg group-hover:bg-teal-900 transition-colors">
                  <MapPin size={20} className="text-teal-500" />
                </div>
                <span className="pt-1">Sainte-Thérèse et environs</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-stone-500">
          <p className="flex items-center gap-1">
            © {new Date().getFullYear()} Irremplaçable. Fait avec <Heart size={14} className="text-red-500 fill-red-500" /> au Québec.
          </p>
          <div className="flex gap-8">
            <Link to="/politique-confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</Link>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 text-xs text-stone-600 mt-6">
          <img
            src={creditPhotoLogo}
            alt="Logo Sabrina Toutant-Gauthier Photographie"
            className="h-7 w-7 rounded-full object-contain shrink-0"
          />
          <span>Crédit photo Sabrina Toutant-Gauthier Photographie</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
